import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Zap, CheckCircle2, Plus } from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Badge } from './ui/badge';
import { format } from 'date-fns';
import { QuickEntryFields } from './quickentry/QuickEntryFields';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ENTRY_TYPES = [
  { id: 'income', label: 'Income Entry', roles: ['admin', 'finance_officer'] },
  { id: 'expense', label: 'Expense Entry', roles: ['admin', 'finance_officer'] },
  { id: 'loan', label: 'New Loan', roles: ['admin', 'loan_officer'] },
  { id: 'maize_purchase', label: 'Maize Purchase', roles: ['admin', 'project_manager', 'finance_officer'] },
  { id: 'sponsorship_child', label: 'Sponsored Child', roles: ['admin', 'project_manager'] },
  { id: 'training_session', label: 'Training Session', roles: ['admin', 'project_manager'] },
  { id: 'staff', label: 'Staff Member', roles: ['admin', 'school_head'] },
  { id: 'student', label: 'Student', roles: ['admin', 'school_head'] },
  { id: 'project', label: 'Project', roles: ['admin', 'project_manager'] },
];

const buildPayload = (entryType, form, today) => {
  switch (entryType) {
    case 'income':
      return ['/api/income', {
        source: form.source,
        amount: parseFloat(form.amount),
        description: form.description || '',
        date: form.date || today,
        related_project: form.related_project || null,
      }];
    case 'expense':
      return ['/api/expenses', {
        category_group: form.category_group,
        subcategory: form.subcategory,
        women_activity: form.women_activity || null,
        details: form.details || {},
        amount: parseFloat(form.amount),
        description: form.description || '',
        date: form.date || today,
        related_project: form.related_project || null,
      }];
    case 'loan':
      return ['/api/loans', {
        borrower_name: form.borrower_name,
        borrower_phone: form.borrower_phone,
        business_type: form.business_type || 'General',
        loan_amount: parseFloat(form.loan_amount),
        interest_rate: parseFloat(form.interest_rate || 10),
        issue_date: form.issue_date || today,
        duration_months: parseInt(form.duration_months || 6),
      }];
    case 'maize_purchase':
      return ['/api/maize/purchases', {
        farmer_id: form.farmer_id,
        quantity_kg: parseFloat(form.quantity_kg),
        price_per_kg: parseFloat(form.price_per_kg),
        purchase_date: form.purchase_date || today,
        notes: form.notes || '',
      }];
    case 'sponsorship_child':
      return ['/api/sponsorship/children', {
        name: form.name,
        date_of_birth: form.date_of_birth || '',
        grade: form.grade || '',
        school: form.school || '',
        sponsor_name: form.sponsor_name || '',
        account_balance: parseFloat(form.account_balance || 0),
        notes: form.notes || '',
      }];
    case 'training_session':
      return ['/api/training/sessions', {
        title: form.title,
        category: form.category,
        description: form.description || '',
        instructor: form.instructor || '',
        start_date: form.start_date || today,
        end_date: form.end_date || null,
        location: form.location || '',
        status: 'upcoming',
      }];
    case 'staff':
      return ['/api/personnel/staff', {
        name: form.name,
        category: form.staff_category || 'administrator',
        phone: form.phone || '',
        home_address: form.home_address || '',
        salary: parseFloat(form.salary || 0),
        class_taught: form.class_taught || '',
        subject: form.subject || '',
        qualification: form.qualification || '',
        title: form.title || '',
        admin_role: form.admin_role || '',
        position: form.position || '',
      }];
    case 'student':
      return ['/api/personnel/students', {
        name: form.name,
        school: form.school_type || 'suubi_primary',
        student_class: form.student_class,
        parent_names: form.parent_names || '',
        parent_contact: form.parent_contact || '',
        home_address: form.home_address || '',
      }];
    case 'project':
      return ['/api/projects', {
        name: form.name,
        project_type: form.project_type,
        description: form.description || '',
        start_date: form.start_date || today,
        end_date: form.end_date || null,
        status: 'active',
        budget: parseFloat(form.budget || 0),
      }];
    default:
      return [null, null];
  }
};

const QuickEntryPanel = ({ user, hasRole, onCreated }) => {
  const [entryType, setEntryType] = useState('income');
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [farmers, setFarmers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [taxonomy, setTaxonomy] = useState(null);

  const allowedTypes = useMemo(() => ENTRY_TYPES.filter((t) => hasRole(t.roles)), [hasRole]);

  useEffect(() => {
    setForm({});
    if (entryType === 'maize_purchase') {
      axios.get(`${API_URL}/api/maize/farmers`).then((r) => setFarmers(r.data)).catch(() => {});
    }
    if (entryType === 'income' || entryType === 'expense') {
      axios.get(`${API_URL}/api/projects`).then((r) => setProjects(r.data)).catch(() => {});
    }
  }, [entryType]);

  useEffect(() => {
    if (entryType === 'expense' && !taxonomy) {
      axios.get(`${API_URL}/api/finance/taxonomy`).then((r) => setTaxonomy(r.data)).catch(() => {});
    }
  }, [entryType, taxonomy]);

  useEffect(() => {
    // Keep entry type within the allowed set for this user
    if (allowedTypes.length > 0 && !allowedTypes.find((t) => t.id === entryType)) {
      setEntryType(allowedTypes[0].id);
    }
  }, [allowedTypes, entryType]);

  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const today = format(new Date(), 'yyyy-MM-dd');

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const [path, payload] = buildPayload(entryType, form, today);
      if (path) await axios.post(`${API_URL}${path}`, payload);
      toast.success('Entry created successfully');
      setForm({});
      if (onCreated) onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create entry');
    } finally {
      setSubmitting(false);
    }
  };

  if (allowedTypes.length === 0) return null;

  return (
    <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent" data-testid="quick-entry-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-primary/10">
              <Zap className="h-4 w-4 text-primary" />
            </div>
            <div>
              <CardTitle className="font-heading text-lg">Quick Data Entry</CardTitle>
              <CardDescription className="text-xs">Add records across all modules from one place</CardDescription>
            </div>
          </div>
          <Badge variant="outline" className="bg-background">
            <CheckCircle2 className="h-3 w-3 mr-1" /> {allowedTypes.length} types available
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Data Type</Label>
            <Select value={entryType} onValueChange={setEntryType}>
              <SelectTrigger data-testid="qe-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {allowedTypes.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <QuickEntryFields
            entryType={entryType}
            form={form}
            setField={setField}
            setForm={setForm}
            farmers={farmers}
            projects={projects}
            taxonomy={taxonomy}
            today={today}
          />
          <Button type="submit" disabled={submitting} className="w-full" data-testid="qe-submit-btn">
            <Plus className="mr-2 h-4 w-4" />
            {submitting ? 'Saving...' : 'Add Entry'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default QuickEntryPanel;
